// Dropdwon.jsx — custom value click → dropdown reopen, tick icon on selected
import React, { useState, useRef, useEffect } from "react";
import {
  encodeCustomDropdownValue,
  isDropdownCustomSelection,
  resolveDropdownDisplayLabel,
} from "../../../../utils/dropdownCustomValue";
import "./FormComponents.css";
import AppIcon from "../../../../Components/AppIcon";

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8"
    width="14" height="14" style={{ flexShrink: 0 }}>
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const Dropdwon = ({
  label, field, options = [], value, onChange,
  isrequired = false, placeholder = "Select...",
  error, className = "", valueKey = null, labelKey = null,
}) => {
  const [open,       setOpen]       = useState(false);
  const [customText, setCustomText] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const ref      = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const getL = o => {
    if (typeof o === "string") return o;
    if (labelKey) return o[labelKey];
    return o.label ?? o.name ?? o.category_name ?? o.type_name ??
      o.state_name ?? o.city_name ?? o.sub_name ?? o.near_name ??
      o.landtype_name ?? String(o);
  };
  const getV = o => {
    if (typeof o === "string") return o;
    if (valueKey) return o[valueKey];
    return o.value !== undefined ? o.value : (o.id !== undefined ? o.id : o);
  };

  const customOption   = options.find(o => o.custom_true === true);
  const regularOptions = options.filter(o => !o.custom_true);

  const isCustomValue = isDropdownCustomSelection(value, regularOptions, getV);

  // Keep custom text in sync when edit data loads after mount
  useEffect(() => {
    if (isCustomValue && customOption) {
      setCustomText(resolveDropdownDisplayLabel(value, regularOptions, getV, getL) || "");
    } else if (!isCustomValue) {
      setCustomText("");
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const selLabel = resolveDropdownDisplayLabel(value, regularOptions, getV, getL);

  const isSelected = o => !isCustomValue && String(getV(o)) === String(value);

  // ── Trigger box click ──────────────────────────────────────
  const handleTriggerClick = () => {
    if (showCustom) {
      // Custom mode active → clicking trigger saves & closes
      handleCustomSubmit();
    } else {
      // ✅ Always open dropdown (even if custom value is selected)
      setOpen(o => !o);
    }
  };

  const handleSelect = (opt) => {
    if (opt.custom_true) {
      // "Other" selected → show inline text input
      setShowCustom(true);
      setCustomText("");
      onChange(field, "", opt);
      setOpen(false);
      setTimeout(() => inputRef.current?.focus(), 80);
    } else {
      setShowCustom(false);
      setCustomText("");
      if (onChange) onChange(field, getV(opt), opt);
      setOpen(false);
    }
  };

  const handleCustomSubmit = () => {
    const trimmed = customText.trim();
    if (!trimmed) return;
    setShowCustom(false);
    onChange(
      field,
      encodeCustomDropdownValue(trimmed),
      { custom: true, label: trimmed, value: encodeCustomDropdownValue(trimmed) },
    );
  };

  const handleCustomKeyDown = (e) => {
    if (e.key === "Enter")  { e.preventDefault(); handleCustomSubmit(); }
    if (e.key === "Escape") { setShowCustom(false); setCustomText(""); onChange(field, ""); }
  };

  // ✅ Custom value display row — click to re-edit or open dropdown
  const handleCustomDisplayClick = () => {
    // Re-open dropdown so user can pick a regular option or re-enter custom
    setOpen(true);
  };

  const clearValue = (e) => {
    e.stopPropagation();
    setShowCustom(false);
    setCustomText("");
    onChange(field, "");
  };

  return (
    <div className={`dropdown-wrapper ${className}`} ref={ref}
      style={{ position: "relative", zIndex: open ? 9999 : "auto" }}>

      {label && (
        <label className="form-label">
          {label}{isrequired && <span className="required-star"> *</span>}
        </label>
      )}

      {/* ── Custom text INPUT (typing mode) ── */}
      {showCustom ? (
        <div className="dd-custom-wrap">
          <input
            ref={inputRef}
            className={`dd-custom-input${error ? " input-error" : ""}`}
            type="text"
            placeholder={customOption?.custom_placeholder || "Type your value…"}
            value={customText}
            onChange={e => setCustomText(e.target.value)}
            onKeyDown={handleCustomKeyDown}
            onBlur={handleCustomSubmit}
          />
          <div className="dd-custom-actions">
            <button type="button" className="dd-custom-confirm" onClick={handleCustomSubmit} title="Save"><AppIcon name="check" size={14} /></button>
            <button type="button" className="dd-custom-clear" onClick={() => { setShowCustom(false); setCustomText(""); }} title="Cancel"><AppIcon name="close" size={14} /></button>
          </div>
        </div>
      ) : (
        /* ── Normal trigger / custom-value-display ── */
        <div
          className={`dropdown-box${open ? " open" : ""}${error ? " input-error" : ""}`}
          onClick={handleTriggerClick}
        >
          {selLabel ? (
            <span className="dd-selected-row">
              {/* ✅ Tick icon for any selected value */}
              {/* <span className="dd-check-icon"><CheckIcon/></span> */}
              <span className="selected-text">{selLabel}</span>
            </span>
          ) : (
            <span className="placeholder-text">{placeholder}</span>
          )}

          {/* Right side: clear X if selected, else chevron */}
          {selLabel ? (
            <span className="dd-trigger-right">
              <button type="button" className="dd-clear-btn" onClick={clearValue} title="Clear">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
              <svg className={`dropdown-arrow${open ? " rotated" : ""}`}
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </span>
          ) : (
            <svg className={`dropdown-arrow${open ? " rotated" : ""}`}
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          )}
        </div>
      )}

      {/* ── Dropdown menu ── */}
      {open && (
        <div className="dropdown-menu">
          {options.length === 0 ? (
            <div className="dropdown-empty">No options available</div>
          ) : (
            <>
              {regularOptions.map((opt, i) => {
                const selected = isSelected(opt);
                return (
                  <div key={i}
                    className={`dropdown-item${selected ? " active" : ""}`}
                    onClick={() => handleSelect(opt)}>
                    <span className="dd-item-label">{getL(opt)}</span>
                    {/* ✅ Tick on active item */}
                    {selected && <span className="dd-item-check"><CheckIcon/></span>}
                  </div>
                );
              })}

              {/* Custom "Other" option */}
              {customOption && (
                <div
                  className={`dropdown-item dd-custom-option${isCustomValue ? " active" : ""}`}
                  onClick={() => handleSelect(customOption)}>
                  <span className="dd-item-label">
                    {/* <span className="dd-custom-icon">✏️</span> */}
                    {getL(customOption)}
                    {isCustomValue && <span className="dd-current-custom"> — "{selLabel}"</span>}
                  </span>
                  {isCustomValue && <span className="dd-item-check"><CheckIcon/></span>}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {error && <span className="error-msg">{error}</span>}
    </div>
  );
};

export default Dropdwon;